import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { mergeExistingFields } from "helpers/mergeExistingFields";

export type PermissionConditions = Record<string, { conditions: string[] }>;

export interface RiskItem {
  id: number;
  riskLevel: string;
  deposit: boolean;
  withdraw: boolean;
  casino: boolean;
  sport: boolean;
  poker: boolean;
  bonus: boolean;
  cashback: boolean;
  promoCode: boolean;
  tournament: boolean;
  transfer: boolean;
  cryptoConvert: boolean;
  affiliateFeatures: boolean;
  userBlocked: boolean;
  active: boolean;
  updatedAt: string;
  permissionConditions?: PermissionConditions;
}

interface RiskState {
  list: RiskItem[];
  isListFetched: boolean;
  isLoading: boolean;
  activePage: number;
  totalPages: number;
  totalItems: number;
  payload?: RiskItem;
  isItemFetching: boolean;
  processingItemId?: number;
}
const initialState: RiskState = {
  list: [],
  isListFetched: false,
  isLoading: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  payload: undefined,
  isItemFetching: false,
  processingItemId: undefined,
};

export const getRiskPermissionList = createAsyncThunk("risk/list", async () => {
  try {
    const result = await instance.get(apiRoutes.riskPermissionsList);
    return result.data;
  } catch (error) {
    return undefined;
  }
});

export const updateRiskPermission = createAsyncThunk(
  "risk/update",
  async (
    {
      riskLevel,
      data,
    }: {
      riskLevel: string;
      data: any;
    },
    { rejectWithValue },
  ) => {
    try {
      await instance.put(
        `${apiRoutes.riskPermissionsUpdate}/${riskLevel}`,
        data,
      );
      return true;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const getRiskPermissionItem = createAsyncThunk(
  "risk/item",
  async (riskLevel: string) => {
    try {
      const result = await instance.get(
        `${apiRoutes.riskPermissionsItem}/${riskLevel}`,
      );
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const riskPermissionsSlice = createSlice({
  name: "risk",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },
    handleUpdateFields: (state, action: PayloadAction<Partial<RiskItem>>) => {
      state.payload = {
        ...state.payload,
        ...action.payload,
      } as RiskItem;
    },

    handleClearFields: (state) => {
      state.payload = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRiskPermissionList.pending, (state) => {
      state.isLoading = true;
      state.isListFetched = false;
    });

    builder.addCase(getRiskPermissionList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isListFetched = true;
      state.list =
        action.payload?.items?.map((item: any, index: number) => ({
          id: index + 1,
          riskLevel: item.riskLevel,
          deposit: item.canDeposit,
          withdraw: item.canWithdraw,
          casino: item.canPlayCasino,
          sport: item.canPlaySport,
          poker: item.canPlayPoker,
          bonus: item.canUseBonus,
          cashback: item.canUseCashback,
          promoCode: item.canUsePromoCode,
          tournament: item.canJoinTournament,
          transfer: item.canTransfer,
          cryptoConvert: item.canCryptoConvert,
          affiliateFeatures: item.canUseAffiliateFeatures,
          userBlocked: item.isUserBlocked,
          active: item.isActive,
          updatedAt: item.updatedAt,
          permissionConditions: item.permissionConditions,
        })) ?? [];
    });

    builder.addCase(getRiskPermissionList.rejected, (state) => {
      state.isLoading = false;
      state.isListFetched = true;
    });
    builder.addCase(updateRiskPermission.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = state.list.find(
        (item) => item.riskLevel === action.meta.arg.riskLevel,
      )?.id;
    });

    builder.addCase(updateRiskPermission.fulfilled, (state, action) => {
      state.isItemFetching = false;
      const { riskLevel, data } = action.meta.arg;
      state.list = state.list.map((item) =>
        item.riskLevel === riskLevel
          ? mergeExistingFields(item, {
              deposit: Boolean(data.canDeposit),
              withdraw: Boolean(data.canWithdraw),
              casino: Boolean(data.canPlayCasino),
              sport: Boolean(data.canPlaySport),
              poker: Boolean(data.canPlayPoker),
              bonus: Boolean(data.canUseBonus),
              cashback: Boolean(data.canUseCashback),
              promoCode: Boolean(data.canUsePromoCode),
              tournament: Boolean(data.canJoinTournament),
              transfer: Boolean(data.canTransfer),
              cryptoConvert: Boolean(data.canCryptoConvert),
              affiliateFeatures: Boolean(data.canUseAffiliateFeatures),
              userBlocked: Boolean(data.isUserBlocked),
              active: Boolean(data.isActive),
              permissionConditions:
                data.permissionConditions ?? item.permissionConditions,
            })
          : item,
      );
      state.processingItemId = undefined;
    });

    builder.addCase(updateRiskPermission.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getRiskPermissionItem.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(getRiskPermissionItem.fulfilled, (state, action) => {
      state.isItemFetching = false;

      if (!action.payload) return;

      const item = action.payload;

      state.payload = {
        id: 0,
        riskLevel: item.riskLevel,
        deposit: item.canDeposit,
        withdraw: item.canWithdraw,
        casino: item.canPlayCasino,
        sport: item.canPlaySport,
        poker: item.canPlayPoker,
        bonus: item.canUseBonus,
        cashback: item.canUseCashback,
        promoCode: item.canUsePromoCode,
        tournament: item.canJoinTournament,
        transfer: item.canTransfer,
        cryptoConvert: item.canCryptoConvert,
        affiliateFeatures: item.canUseAffiliateFeatures,
        userBlocked: item.isUserBlocked,
        active: item.isActive,
        updatedAt: item.updatedAt,
        permissionConditions: item.permissionConditions,
      };
    });

    builder.addCase(getRiskPermissionItem.rejected, (state) => {
      state.isItemFetching = false;
    });
  },
});

export const { handlePaginate, handleUpdateFields, handleClearFields } =
  riskPermissionsSlice.actions;
